import { useState } from 'react';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { useHistory, useLocation } from 'react-router-dom';
import { X, Menu, Loader2, LogOut, Check, RefreshCw, ArrowLeftRight, Eye } from 'lucide-react';

import { setCurrentDistro } from '../../redux/distro/distro.actions';
import { selectCurrentDistro } from '../../redux/distro/distro.selector';
import { setCurrentUser } from '../../redux/user/user.actions';
import { selectCurrentUser } from '../../redux/user/user.selectors';
import AccountSwitcherModal from '../ui/AccountSwitcherModal';
import nhcLogo from "../../assets/nhclogo.png";
import { CLIENTS_PATH, isClientOnlyForDistro } from "../../utils/firmAccess";

const Header = ({ onSave, onSend, mode, onClick, showSidebar, saving, sending, setCurrentUser, setCurrentDistro, currentUser, currentDistro }) => {
    const [showSwitchModal, setShowSwitchModal] = useState(false);
    const history = useHistory();
    const location = useLocation();
    const isEnrollPage = location.pathname.includes('/enroll/');

    // show switch button only for owners with multiple distros
    const canSwitchAccount = currentUser?.role === 'owner' && currentUser?.distros?.length > 1;
    const canSwitchRole = currentUser?.hasDualRole;

    const handleLogout = () => {
        setCurrentDistro(null);
        setCurrentUser();
    };

    const handleSwitchRole = () => {
        const newRole = currentUser.role === 'owner' ? 'advisor' : 'owner';
        setCurrentDistro(null);
        setCurrentUser({ ...currentUser, role: newRole });
        history.push(newRole === 'owner' ? '/user/dashboard' : '/user/profile');
    };

    return (
       <header className="fixed top-0 left-0 right-0 h-16 bg-white/80 backdrop-blur-xl border-b border-gray-200/60 px-4 sm:px-6 flex items-center justify-between z-50">
            {/* Left Section: Logo + Name */}
            <div className="flex items-center gap-3"> 
                <a href={isClientOnlyForDistro(currentUser, currentDistro) ? CLIENTS_PATH : "/user/dashboard"} className="flex items-center">
                    <img
                        className="h-9 w-auto" 
                        src={nhcLogo}
                        alt="National HealthClaim"
                    />
                </a>
                
                {/* Vertical Divider (Optional - adds a very clean "Pro" look) */}
                <div className="h-6 w-[1px] bg-gray-300 mx-1" />

                {currentDistro && 
                <span className="text-lg sm:text-xl font-semibold text-primary tracking-tight truncate max-w-[200px] sm:max-w-none">
                    {currentUser.distros.find(x => x.id === currentDistro)?.name}
                </span>}

                {/* account switch button - desktop only, owners with 2+ distros */}
                {!isEnrollPage && canSwitchAccount && (
                    <button
                        onClick={() => setShowSwitchModal(true)}
                        className="hidden lg:inline-flex items-center gap-1.5 px-3 py-2 text-gray-500 hover:text-gray-900 text-sm font-medium transition-colors"
                    >
                        <RefreshCw className="w-4 h-4" />
                        <span>Switch Firm</span>
                    </button>
                )}

                {/* role switch button - desktop only, dual-role users */}
                {!isEnrollPage && canSwitchRole && (
                    <button
                        onClick={handleSwitchRole}
                        className="hidden lg:inline-flex items-center gap-1.5 px-3 py-2 text-gray-500 hover:text-gray-900 text-sm font-medium transition-colors"
                    >
                        <ArrowLeftRight className="w-4 h-4" />
                        <span>Switch to {currentUser.role === 'owner' ? 'Advisor' : 'Firm'} Portal</span>
                    </button>
                )}
            </div>

            {/* right side actions */}
            <div className="flex items-center gap-3">
                {currentUser?.opsActor && (
                    <span
                        title={`Logged in as ${currentUser.opsUserName || currentUser.username}, viewing ${currentUser.opsScopeName || 'this account'}`}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-100 text-amber-900 border border-amber-300 text-xs font-semibold whitespace-nowrap"
                    >
                        <Eye className="w-3.5 h-3.5 flex-shrink-0" />
                        <span className="hidden sm:inline">Logged in as </span>
                        <span className="truncate max-w-[160px]">{currentUser.opsUserName || currentUser.username}</span>
                    </span>
                )}

                {/* enroll page action buttons */}
                {isEnrollPage && (
                    <div className="flex items-center gap-2.5">
                        <button
                            className="inline-flex items-center justify-center gap-2 h-10 min-w-[100px] px-5 bg-gray-100 text-gray-700 text-sm font-semibold rounded-full hover:bg-gray-200 active:scale-[0.97] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                            onClick={onSave}
                            disabled={saving}
                        >
                            {saving ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                                <span>Save Draft</span>
                            )}
                        </button>
                        {mode === 'edit' && (
                            <button
                                className="inline-flex items-center justify-center gap-2 h-10 min-w-[100px] px-5 bg-green-600 text-white text-sm font-semibold rounded-full shadow-sm hover:bg-green-700 hover:shadow-md active:scale-[0.97] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                onClick={onSend}
                                disabled={sending}
                            >
                                {sending ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                    <>
                                        <Check className="w-4 h-4" strokeWidth={2.5} />
                                        <span>Send</span>
                                    </>
                                )}
                            </button>
                        )}
                    </div>
                )}

                {/* logout button - desktop only, hidden on enroll page */}
                {!isEnrollPage && (
                    <button
                        onClick={handleLogout}
                        className="hidden lg:inline-flex items-center gap-1.5 px-3 py-2 text-gray-500 hover:text-gray-900 text-sm font-medium transition-colors"
                    >
                        <LogOut className="w-4 h-4" />
                        <span>Logout</span>
                    </button>
                )}

                {/* mobile menu button */}
                {!isEnrollPage && (
                    <button
                        onClick={onClick}
                        className="lg:hidden p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                        aria-label="Toggle menu"
                    >
                        {showSidebar ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                    </button>
                )}
            </div>

            <AccountSwitcherModal
                isOpen={showSwitchModal}
                onClose={() => setShowSwitchModal(false)}
            />
        </header>
    );
};

const mapStateToProps = createStructuredSelector({
    currentUser: selectCurrentUser,
    currentDistro: selectCurrentDistro,
});

const mapDispatchToProps = (dispatch) => ({
    setCurrentUser: (data) => dispatch(setCurrentUser(data)),
    setCurrentDistro: (data) => dispatch(setCurrentDistro(data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Header);
